import Phaser from 'phaser';
import Configs from '../../../configs/Configs';
import { SKILLS } from '../../../configs/Configs';
import Controller from '../controller/Controller';
// import Hero from '../heroes/hero/Hero';

const EVENTS = {
    GOLD_UPDATED: 'gold_updated',
    EXP_UPDATED: 'exp_updated',
    ENRAGE_REFRESH_USED: 'enrage_refresh_used',
};

type AliceSkill = keyof typeof SKILLS.ALICE;
type BobSkill = keyof typeof SKILLS.BOB;
type SkillName = (typeof SKILLS.ALICE)[AliceSkill] | (typeof SKILLS.BOB)[BobSkill];

type HeroUpgradeAlice = {
    damage: number;
    critChance: number;
    extraGold: number;
};

type HeroUpgradeBob = {
    damage: number;
    enrageTime: number;
    enrageRefresh: number;
};

type HeroData = AliceHero | BobHero;

type StoryData = {
    totalChapters: number;
    unlocked: number[];
};

export default class Session {
    private scene: Phaser.Scene;

    static instance: Session;
    static get Instance(): Session {
        return Session.instance;
    }

    static get EVENTS() {
        return EVENTS;
    }

    gold: number = 0;
    stage: number = 0;
    chapter: number = 1;
    heroes: HeroData[] = [];
    story: StoryData = { totalChapters: 6, unlocked: [1] };

    constructor(scene: Phaser.Scene) {
        this.scene = scene;

        this.gold = Configs.debugBaseGold || 0;
        this.stage = Configs.debugBaseStage || 0;
        this.chapter = 1;

        this.heroes = [
            {
                id: 0,
                upgrades: {
                    damage: 1,
                    critChance: 1,
                    extraGold: 1,
                },
                items: [],
            },
            {
                id: 1,
                upgrades: {
                    damage: 1,
                    enrageTime: 1,
                    enrageRefresh: 1,
                },
                items: [],
            },
        ];

        this.story = {
            totalChapters: 6,
            unlocked: [1],
        };

        Session.instance = this;

        this.setupListeners();
    }

    setupListeners(): void {
        this.scene.events.once('shutdown', this.destroy, this);
    }

    advanceStage(): void {
        this.stage++;
    }

    addGold(val: number): void {
        this.gold += val;
        this.scene.events.emit(EVENTS.GOLD_UPDATED, this.gold);
    }

    spendGold(val: number): void {
        this.gold -= val;
        this.scene.events.emit(EVENTS.GOLD_UPDATED, this.gold);
    }

    unlockChapter(chapterId: number): void {
        if (chapterId === 0) return;
        this.chapter = chapterId;
        this.stage = -1;
        if (this.story.unlocked.indexOf(chapterId) === -1) this.story.unlocked.push(chapterId);
        this.scene.events.emit(Controller.EVENTS.SHOW_CUTSCENE, chapterId);
    }

    upgradeBobSkill(skill: SkillName): boolean {
        let success = false;
        let hero = this.heroes[1] as BobHero;

        let skillLevel: number;
        let price: number | null;

        switch (skill) {
            case SKILLS.BOB.DAMAGE:
                skillLevel = hero.upgrades.damage;
                price = Configs.getBobSkill(skill, skillLevel).upgradePrice;
                if (price != null && this.gold >= price) {
                    this.spendGold(price);
                    hero.upgrades.damage++;
                    success = true;
                }
                break;
            case SKILLS.BOB.ENRAGE_TIME:
                skillLevel = hero.upgrades.enrageTime;
                price = Configs.getBobSkill(skill, skillLevel).upgradePrice;
                if (price != null && this.gold >= price) {
                    this.spendGold(price);
                    hero.upgrades.enrageTime++;
                    success = true;
                }
                break;
        }

        return success;
    }

    useEnrageRefresh(): boolean {
        let success: boolean = false;
        let hero = this.heroes[1] as BobHero;

        let skill = SKILLS.BOB.ENRAGE_REFRESH;
        let skillLevel = hero.upgrades.enrageRefresh;
        let price = Configs.getBobSkill(skill, skillLevel).value;

        if (price != null && this.gold >= price) {
            this.spendGold(price);
            hero.upgrades.enrageRefresh++;
            success = true;
        }

        if (success) this.scene.events.emit(EVENTS.ENRAGE_REFRESH_USED);

        return success;
    }

    getSkillLevel(name: SkillName): number | undefined {
        let aliceHero = this.heroes[0] as AliceHero;
        let bobHero = this.heroes[1] as BobHero;

        switch (name) {
            case SKILLS.ALICE.DAMAGE:
                return aliceHero.upgrades.damage;
            case SKILLS.ALICE.CRIT_CHANCE:
                return aliceHero.upgrades.critChance;
            case SKILLS.ALICE.EXTRA_GOLD:
                return aliceHero.upgrades.extraGold;
            case SKILLS.BOB.DAMAGE:
                return bobHero.upgrades.damage;
            case SKILLS.BOB.ENRAGE_TIME:
                return bobHero.upgrades.enrageTime;
            case SKILLS.BOB.ENRAGE_REFRESH:
                return bobHero.upgrades.enrageRefresh;
            default:
                return undefined;
        }
    }

    destroy(): void { }
}
